import { Box, InputAdornment, Typography } from "@mui/material";
import i18n from "i18next";
import { useState } from "react";
import type { PartConfigurationSearchApi_PartConfigurationSearchResult } from "src/api/generated/erp/configurator/api/partConfigurationSearchApi.ts";
import { PartConfigurationSearchApi } from "src/api/generated/erp/configurator/api/partConfigurationSearchApi.ts";
import { getConfigurableObjectTypeLabel } from "src/api/generated/erp/db/types/enums/configurableObjectType.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { AavoFormFooter } from "src/components/common/forms/AavoFormFooter.tsx";
import { AavoNumberField } from "src/components/common/inputFields/AavoNumberField.tsx";
import { formatIsoString } from "src/utils/dayjsUtils.ts";
import { AsyncRender } from "src/components/common/async/AsyncRender.tsx";
import { useAsyncState } from "src/utils/async/asyncState.ts";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

export interface ImportPartConfigurationDialogProps {
	onSubmit: (request: { partConfigurationId: number }) => Promise<void>;
	closeDialog: () => Promise<void>;
}

export const ImportPartConfigurationDialog = ({ onSubmit, closeDialog }: ImportPartConfigurationDialogProps) => {
	const [configurationIdInput, setConfigurationIdInput] = useState<number | null>(null);
	const [searchResultAsync, setSearchResultAsync] =
		useAsyncState<PartConfigurationSearchApi_PartConfigurationSearchResult | null>({});

	return (
		<VerticalBox sx={{ flex: 1 }}>
			<VerticalBox sx={{ padding: 2, gap: 2, flex: 1 }}>
				<AavoNumberField
					label={i18n.t("configuration_id")}
					value={configurationIdInput}
					onChange={(val) => setConfigurationIdInput(val)}
					type="integer"
					onSubmit={async (_, source) => {
						if (source === "enterKey") {
							await handleSearch();
						}
					}}
					slotProps={{
						input: {
							endAdornment: (
								<InputAdornment position={"end"}>
									<AsyncButton
										icon={faMagnifyingGlass}
										label={i18n.t("search.verb")}
										onClick={handleSearch}
										disabled={configurationIdInput == null || configurationIdInput <= 0}
									/>
								</InputAdornment>
							),
						},
					}}
				/>

				<AsyncRender
					asyncData={searchResultAsync}
					noContainer
					content={(result) => {
						if (result === null) {
							return <Typography color="error">{i18n.t("configuration_not_found")}</Typography>;
						}
						return <SearchResultContent result={result} />;
					}}
				/>
			</VerticalBox>

			<AavoFormFooter>
				<AsyncButton label={i18n.t("cancel")} color={"secondary"} onClick={closeDialog} />
				<AsyncButton label={i18n.t("import")} onClick={handleSubmit} disabled={!searchResultAsync.data} />
			</AavoFormFooter>
		</VerticalBox>
	);

	async function handleSearch() {
		const id = configurationIdInput;
		if (id == null || id <= 0) return;
		await setSearchResultAsync(() =>
			PartConfigurationSearchApi.searchPartConfiguration({ partConfigurationId: id }),
		);
	}

	async function handleSubmit() {
		const result = searchResultAsync.data;
		if (!result) return;

		await onSubmit({
			partConfigurationId: result.partConfiguration.partConfigurationId,
		});

		await closeDialog();
	}
};

interface SearchResultContentProps {
	result: PartConfigurationSearchApi_PartConfigurationSearchResult;
}

const SearchResultContent = ({ result }: SearchResultContentProps) => {
	return (
		<VerticalBox sx={{ gap: 1 }}>
			<Typography variant="subtitle2">{i18n.t("configuration_description")}</Typography>
			<Typography>{result.partConfiguration.description}</Typography>
			<Typography variant="body2" color="text.secondary">
				{formatIsoString(result.partConfiguration.createdAt)}
			</Typography>
			{result.connectedItems.length > 0 && (
				<Box sx={{ mt: 1 }}>
					<Typography variant="subtitle2">{i18n.t("connected_items")}</Typography>
					<Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
						{result.connectedItems.map((item, idx) => (
							<li key={idx}>
								<Typography variant="body2">
									{getConfigurableObjectTypeLabel(item.objectType)}: {item.description}
								</Typography>
							</li>
						))}
					</Box>
				</Box>
			)}
		</VerticalBox>
	);
};
